import React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * The pieces both candidate-facing steps are built from.
 *
 * Steps 1 and 3 are the same shape — send a link, read what came back, record
 * what you decided — and they carry two identical sections at the foot
 * ("Manager checks & reference review" and "Request & decision history"). These
 * live here so the two panels cannot drift into looking like different screens.
 */

export const cardCls = 'bg-white dark:bg-[#262b33] border border-[#E3E9F3] dark:border-gray-700 rounded-2xl';
export const inputCls = 'w-full px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px]';
export const fieldLabelCls = 'block text-[11.5px] font-bold uppercase tracking-wide text-[#64748B] dark:text-gray-400 mb-1.5';

const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

/** Uploaded files come back as /api/files/... paths; absolute URLs pass through. */
export const fileHref = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_ORIGIN}${url}`);

export const fmtDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * A moment, with the time of day when the record actually holds one.
 *
 * Half of what recruitment stores is a TIMESTAMP — a form sent, a reference
 * received, a decision recorded — and half is a DATE somebody typed, like an
 * interview date or a DBS issue date. Printing "16 Sep 2026, 00:00" against a
 * typed date would be inventing a time nobody entered, so a value with no time
 * part is formatted as a plain date.
 *
 * Two events on the same day are otherwise indistinguishable, which is the
 * whole problem with a timeline that only shows dates: "we sent it and they
 * returned it" reads as simultaneous.
 */
export const fmtDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  // A DATEONLY column arrives as "2026-09-16" (or midnight UTC) — there is no
  // time in it to show.
  const text = String(value);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(text);
  if (dateOnly) return fmtDate(value);

  return date.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

const TONES = {
  good: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400',
  warn: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400',
  bad: 'bg-rose-100 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400',
  info: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
  neutral: 'bg-slate-100 dark:bg-[#31373f] text-slate-600 dark:text-gray-300',
};

export const Pill = ({ tone = 'neutral', children }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11.5px] font-bold ${TONES[tone]}`}>
    {children}
  </span>
);

/**
 * A collapsible review section.
 *
 * Uses <details>/<summary> rather than a state-driven toggle: the disclosure
 * behaviour, keyboard handling and screen-reader semantics come for free, and
 * an open section survives the re-render when the candidate record refreshes
 * after a save.
 */
export const Section = ({ icon: Icon, title, count, subtitle, defaultOpen, children }) => (
  <details className={`${cardCls} group`} open={defaultOpen}>
    <summary className="flex items-center gap-3 p-5 cursor-pointer list-none select-none">
      <ChevronRight className="w-4 h-4 text-[#94A3B8] shrink-0 transition-transform group-open:rotate-90" />
      <Icon className="w-4 h-4 text-blue-600 shrink-0" />
      <span className="text-[14px] font-semibold text-[#0B1220] dark:text-gray-100">
        {title}
        {count !== undefined && (
          <span className="ml-1.5 text-[13px] font-medium text-[#94A3B8] dark:text-gray-500 tabular-nums">({count})</span>
        )}
      </span>
    </summary>
    <div className="px-5 pb-5 pt-0">
      {subtitle && <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mb-4">{subtitle}</p>}
      {children}
    </div>
  </details>
);

/**
 * The status line for a candidate link, shared so "Returned · 3 Sep" means the
 * same thing on both steps.
 *
 * @param {{status: string, sent_at: ?string, submitted_at: ?string, complete: boolean}} link
 */
export const linkStatus = (link = {}) => {
  if (link.complete) {
    return { tone: 'good', label: `Returned${link.submitted_at ? ` · ${fmtDate(link.submitted_at)}` : ''}` };
  }
  if (!link.status || link.status === 'Not sent') return { tone: 'neutral', label: 'Not sent' };
  return { tone: 'warn', label: `${link.status}${link.sent_at ? ` · sent ${fmtDate(link.sent_at)}` : ''}` };
};
